import { Accordion, AccordionProps, Box, Button, ButtonProps, CardProps, Center, Flex, IconButton, Image, Spinner, Text, VStack } from '@chakra-ui/react'
import { isToday } from 'date-fns'
import { Match, pipe } from 'effect'
import { useSkiptraceStore } from 'features/Skiptrace/infra/react/Skiptrace.state'
import { DateLib } from 'libs/Date'
import { StringLib } from 'libs/String'
import hurtDogImg from 'presentation/assets/img/3d/hurt-dog.png'
import { Breakpoint } from 'presentation/components/Breakpoint'
import { EnterIcon } from 'presentation/components/Icons'
import { CardPrimaryTitle, CardSecondarySupportingText, Card as ChakraCard } from 'presentation/components/molecules/Card'
import { useSelectFormatModal } from 'presentation/screens/SkiptraceScreen/components/SelectFormatModal/SelectFormatModal.api'
import { SkiptraceAccordionItem } from 'presentation/screens/SkiptraceScreen/components/SkiptraceResultsCard/components/SkiptraceAccordionItem'
import { mbp } from 'presentation/utils/mapBreakpoint'

export const SkiptraceResultsCard = (props: CardProps) => {
  const store = useSkiptraceStore(store => {
    const resultState = store.entities.resultState

    // Handle error state
    if (resultState.status === 'error') {
      return {
        status: 'error' as const,
        error: resultState.error,
      }
    }

    // Handle success state
    if (resultState.status === 'success' && resultState.result) {
      return {
        status: 'success' as const,
        owners: resultState.result.owners,
        tracers: resultState.result.tracers,
        address: resultState.result.subject.address,
        updateDate: resultState.result.skiptracedAt,
      }
    }

    // Handle loading/skiptracing states
    return {
      status: 'loading' as const,
    }
  })

  const selectFormatModalApi = useSelectFormatModal()
  const openDownloadModal = selectFormatModalApi.actions.open

  return (
    <CustomCard {...props}>
      {pipe(
        Match.value(store),
        Match.when({ status: 'success' }, result => {
          const tracersPhrase = StringLib.stringsToPhrase(result.tracers.map(tracer => tracer.name))

          return (
            <Flex flexDirection='column' h='full' minH={0}>
              <Box>
                <Flex justifyContent='space-between'>
                  <CardPrimaryTitle>
                    Skiptrace results
                  </CardPrimaryTitle>
                  <Breakpoint
                    mobSm={(
                      <DownloadIconButton onClick={() => openDownloadModal()} />
                    )}
                    tabSm={(
                      <DownloadButton onClick={() => openDownloadModal()} />
                    )}
                  />
                </Flex>
                <CardSecondarySupportingText>
                  Skipped by
                  {' '}
                  {tracersPhrase}
                  {' '}
                </CardSecondarySupportingText>
              </Box>
              <CustomAccordion>
                {result.owners.map(owner => (
                  <SkiptraceAccordionItem
                    key={owner.name}
                    owner={owner}
                  />
                ))}
              </CustomAccordion>
              {result.owners.length === 0 && (
                <Text textStyle='bodyL' color='graystrong.400'>
                  No Results Found. No Charge.
                  {result.updateDate && `
                    Last skiptraced ${isToday(result.updateDate)
                  ? 'today'
                  : `on ${DateLib.formatNaturalShortMonth(result.updateDate)}`
                }`}
                  .
                </Text>
              )}
            </Flex>
          )
        }),
        Match.when({ status: 'error' }, () => (
          <Center h='full' flexDirection='column' py={8}>
            <VStack spacing={mbp({ tabSm: 2, mob: 1 })}>
              <Image
                src={hurtDogImg}
                alt='something went wrong'
                boxSize={mbp({
                  tabSm: '120px',
                  mob: '96px',
                  mobSm: '80px',
                })}
              />
              <Text
                textStyle={mbp({
                  tabSm: 'h4',
                  mob: 'bodyXLFat',
                  mobSm: 'bodyLFat',
                })}
                color='graystrong.500'
                textAlign='center'
              >
                Something went wrong
              </Text>
              <Text
                textStyle={mbp({
                  tabSm: 'bodyLFat',
                  mobSm: 'bodyMFat',
                })}
                color='graystrong.200'
                textAlign='center'
              >
                We encountered an error while processing your skiptrace. Please try again later or contact support if the issue persists.
              </Text>
            </VStack>
          </Center>
        )),
        Match.orElse(() => (
          <Center h='full'>
            <Spinner />
          </Center>
        )),
      )}
    </CustomCard>
  )
}

export const CustomAccordion = (props: AccordionProps) => ((
  <Accordion
    allowToggle
    display='flex'
    flexDirection='column'
    gap={3}
    mt={mbp({ mobSm: 2, tabSm: 3 })}
    flex='1 1 0'
    reduceMotion
    {...props}
  >
    {props.children}
  </Accordion>
))

const CustomCard = (props: CardProps) => (
  <Breakpoint
    mobSm={(
      <ChakraCard
        size='sm-locked'
        sx={{
          bg: 'none',
          shadow: 'none',
          p: 0,
        }}
        {...props}
      >
        {props.children}
      </ChakraCard>
    )}
    tabSm={(
      <ChakraCard
        size='sm-locked'
        {...props}
      >
        {props.children}
      </ChakraCard>
    )}
  />
)

const DownloadIconButton = (props: ButtonProps) => (
  <IconButton
    aria-label='Download'
    icon={(
      <EnterIcon
        boxSize={3}
        transform='rotate(90deg)'
      />
    )}
    variant='icon-ghost'
    colorScheme='neutral'
    size='md'
    {...props}
  />
)

const DownloadButton = (props: ButtonProps) => (
  <Button
    colorScheme='neutral'
    variant='ghost'
    leftIcon={(
      <EnterIcon
        boxSize={3}
        transform='rotate(90deg)'
      />
    )}
    {...props}
  >
    Download
  </Button>
)
